import {
  useCreateOrganisationBankAccount,
  useDeleteOrganisationBankAccount,
  useOrganisationBankAccounts,
  useUpdateOrganisationBankAccount,
} from '@my/directory-api/hooks';
import type { OrganisationBankAccount } from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  SelectInput,
  Switch,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Return `options` with `code` appended if it's not already present. Used
 * for the per-row dropdown so legacy bank accounts with a no-longer-enabled
 * currency can still be saved without forcing a switch. Caller supplies the
 * fallback label so the message stays localised.
 */
function withCurrency(
  options: SelectInputOption[],
  code: string,
  fallbackLabel: string,
): SelectInputOption[] {
  if (!code) return options;
  const exists = options.some((o) => (typeof o === 'string' ? o : o.value) === code);
  if (exists) return options;
  return [...options, { value: code, label: fallbackLabel }];
}

export interface BankAccountsSectionProps {
  /** Owning organisation id; the panel stays disabled while this is null. */
  organisationId: string | null;
  /**
   * Currencies enabled at the organisation level — base currency plus any
   * additionals. Bank accounts are constrained to this set. Existing rows
   * whose stored currency is no longer enabled get their currency added
   * back to the dropdown for that row only, so the operator can save edits
   * without being forced to switch.
   */
  currencyOptions: SelectInputOption[];
}

/**
 * Bottom-row card on the Organisation page. Lists existing bank accounts for
 * the org and allows in-place create / update / delete. Each row saves
 * independently (as in Old TMS) — the bank-account mutations are not
 * batched into the page's main green-tick save.
 *
 * FE validation mirrors the BE FluentValidation rules:
 *  - Name / BankName: required, max 128 chars
 *  - AccountNumber: required, max 64 chars
 *  - SortCode: optional, max 16
 *  - IBAN: optional, max 34
 *  - BIC: optional, max 11
 *  - CurrencyIsoCode: required, exactly 3 chars (always satisfied by the dropdown)
 */
export function BankAccountsSection({ organisationId, currencyOptions }: BankAccountsSectionProps) {
  const { t } = useTranslation('organisation');
  const listQuery = useOrganisationBankAccounts(organisationId);
  const createMutation = useCreateOrganisationBankAccount();
  const updateMutation = useUpdateOrganisationBankAccount();
  const deleteMutation = useDeleteOrganisationBankAccount();

  // `null` = no editor open; `'new'` = drafting a brand-new account; a guid
  // = editing the row with that id. Only one editor open at a time.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<BankAccountForm>(emptyForm(currencyOptions));
  const [submitted, setSubmitted] = useState(false);
  // Row pending a confirmation modal — `null` while the dialog is closed.
  const [pendingDelete, setPendingDelete] = useState<OrganisationBankAccount | null>(null);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const startEdit = (row: OrganisationBankAccount) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm(currencyOptions));
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm(currencyOptions));
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;

    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync({
          organisationId,
          name: draft.name.trim(),
          bankName: draft.bankName.trim(),
          accountNumber: draft.accountNumber.trim(),
          sortCode: draft.sortCode.trim() || null,
          iban: draft.iban.trim() || null,
          bic: draft.bic.trim() || null,
          currencyIsoCode: draft.currencyIsoCode.trim().toUpperCase(),
          isDefaultForCurrency: draft.isDefaultForCurrency,
        });
        showToast({ type: 'success', message: t('bankAccounts.added') });
      } else {
        await updateMutation.mutateAsync({
          organisationId,
          body: {
            bankAccountId: editingId,
            name: draft.name.trim(),
            bankName: draft.bankName.trim(),
            accountNumber: draft.accountNumber.trim(),
            sortCode: draft.sortCode.trim() || null,
            iban: draft.iban.trim() || null,
            bic: draft.bic.trim() || null,
            currencyIsoCode: draft.currencyIsoCode.trim().toUpperCase(),
            isDefaultForCurrency: draft.isDefaultForCurrency,
          },
        });
        showToast({ type: 'success', message: t('bankAccounts.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('bankAccounts.saveFailed'),
      });
    }
  };

  // Stage a row for confirmation; the actual delete fires from
  // `confirmDeletePending` once the operator clicks Delete in the modal.
  const requestDelete = (row: OrganisationBankAccount) => {
    if (!organisationId) return;
    setPendingDelete(row);
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('bankAccounts.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('bankAccounts.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Hide the header "+ Add" button while an editor is open — there's no
  // sense in starting a new draft when one is already in flight.
  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('bankAccounts.addAria')}
        onClick={startNew}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('bankAccounts.add')}
      </button>
    ) : null;

  return (
    <FormSection title={t('bankAccounts.title')} columns={1} actions={headerAction}>
      {listQuery.isPending && <Loading label={t('bankAccounts.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('bankAccounts.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && (
        <ul className="space-y-2">
          {/*
            New-account draft renders at the TOP of the list. Edits to
            existing rows render IN-PLACE further down — the editor takes
            over the row's slot rather than jumping to the top. The form
            already paints its own bordered slate-50 panel, so the wrapping
            <li> doesn't add a frame here.
          */}
          {editingId === 'new' && (
            <li>
              <BankAccountForm
                draft={draft}
                onChange={setDraft}
                submitted={submitted}
                currencyOptions={currencyOptions}
                onSave={handleSave}
                onCancel={cancel}
                isBusy={isBusy}
              />
            </li>
          )}

          {listQuery.data.length === 0 && editingId !== 'new' && (
            <li className="text-sm text-slate-500">{t('bankAccounts.empty')}</li>
          )}

          {listQuery.data.map((row) =>
            editingId === row.id ? (
              <li key={row.id}>
                <BankAccountForm
                  draft={draft}
                  onChange={setDraft}
                  submitted={submitted}
                  // Include this row's stored currency in the options even if
                  // it's no longer in the org's enabled set — prevents the
                  // dropdown from going blank for legacy rows.
                  currencyOptions={withCurrency(
                    currencyOptions,
                    row.currencyIsoCode,
                    t('bankAccounts.currencyDisabledLabel', { code: row.currencyIsoCode }),
                  )}
                  onSave={handleSave}
                  onCancel={cancel}
                  onDelete={() => requestDelete(row)}
                  isBusy={isBusy}
                />
              </li>
            ) : (
              <li
                key={row.id}
                className="flex items-center gap-2 rounded-sm border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{row.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {row.bankName} · {row.accountNumber}
                    {row.sortCode ? ` · ${row.sortCode}` : ''}
                  </p>
                </div>
                <CurrencyBadge code={row.currencyIsoCode} />
                {/*
                  Edit-only on the summary row; delete lives inside the
                  editor (trash icon). Stops single-click destructive
                  actions on the always-visible row.
                */}
                <button
                  type="button"
                  aria-label={t('bankAccounts.ariaEdit', { name: row.name })}
                  onClick={() => startEdit(row)}
                  className="flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('bankAccounts.deleteDialog.title')}
        message={
          pendingDelete
            ? t('bankAccounts.deleteDialog.message', { name: pendingDelete.name })
            : null
        }
        confirmLabel={t('bankAccounts.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Form
// ────────────────────────────────────────────────────────────────────────────

/**
 * Editable shape for the bank-account inline editor. Every field is held as
 * a string (even though the BE accepts `null` for optionals) so the inputs
 * can stay controlled without juggling `null | undefined | ''`.
 */
interface BankAccountForm {
  name: string;
  bankName: string;
  accountNumber: string;
  sortCode: string;
  iban: string;
  bic: string;
  currencyIsoCode: string;
  /** Prints on documents in this currency. Only matters with 2+ in a currency. */
  isDefaultForCurrency: boolean;
}

function emptyForm(currencyOptions: SelectInputOption[]): BankAccountForm {
  // Default the currency to the first option so the form is valid the moment
  // it opens — `CurrencyIsoCode` requires exactly 3 chars on the BE, an
  // empty string would always fail.
  const first = currencyOptions[0];
  const fallback = typeof first === 'string' ? first : (first?.value ?? 'EUR');
  return {
    name: '',
    bankName: '',
    accountNumber: '',
    sortCode: '',
    iban: '',
    bic: '',
    currencyIsoCode: fallback,
    isDefaultForCurrency: false,
  };
}

function toForm(row: OrganisationBankAccount): BankAccountForm {
  return {
    name: row.name,
    bankName: row.bankName,
    accountNumber: row.accountNumber,
    sortCode: row.sortCode ?? '',
    iban: row.iban ?? '',
    bic: row.bic ?? '',
    currencyIsoCode: row.currencyIsoCode,
    isDefaultForCurrency: row.isDefaultForCurrency,
  };
}

function isFormValid(form: BankAccountForm): boolean {
  return (
    form.name.trim().length > 0 &&
    form.bankName.trim().length > 0 &&
    form.accountNumber.trim().length > 0 &&
    form.currencyIsoCode.trim().length === 3 &&
    form.name.length <= 128 &&
    form.bankName.length <= 128 &&
    form.accountNumber.length <= 64 &&
    form.sortCode.length <= 16 &&
    form.iban.length <= 34 &&
    form.bic.length <= 11
  );
}

interface BankAccountFormProps {
  draft: BankAccountForm;
  onChange: (next: BankAccountForm) => void;
  submitted: boolean;
  currencyOptions: SelectInputOption[];
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

function BankAccountForm({
  draft,
  onChange,
  submitted,
  currencyOptions,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: BankAccountFormProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof BankAccountForm>(key: K, value: BankAccountForm[K]) =>
    onChange({ ...draft, [key]: value });

  // Field-level validity flags. `submitted` gates whether the red-line
  // decoration shows — a pristine form (untouched, before save) doesn't
  // scream at the user before they've had a chance to fill it in.
  const errs = {
    name: !draft.name.trim(),
    bankName: !draft.bankName.trim(),
    accountNumber: !draft.accountNumber.trim(),
    currency: draft.currencyIsoCode.trim().length !== 3,
  };

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="space-y-3">
        <TextInput
          label={t('bankAccounts.fields.accountName')}
          name="name"
          autoComplete="off"
          value={draft.name}
          maxLength={128}
          onChange={(e) => set('name', e.target.value)}
          error={submitted && errs.name}
          errorMessage={t('bankAccounts.validation.accountNameRequired')}
        />
        <TextInput
          label={t('bankAccounts.fields.bankName')}
          name="bankName"
          autoComplete="off"
          value={draft.bankName}
          maxLength={128}
          onChange={(e) => set('bankName', e.target.value)}
          error={submitted && errs.bankName}
          errorMessage={t('bankAccounts.validation.bankNameRequired')}
        />
        {/*
          Account Number + Sort Code share a row, IBAN + BIC share a row.
          The pairings reflect how operators actually look these up — domestic
          identifiers together, international identifiers together.
        */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label={t('bankAccounts.fields.accountNumber')}
            name="accountNumber"
            autoComplete="off"
            value={draft.accountNumber}
            maxLength={64}
            onChange={(e) => set('accountNumber', e.target.value)}
            error={submitted && errs.accountNumber}
            errorMessage={t('bankAccounts.validation.accountNumberRequired')}
          />
          <TextInput
            label={t('bankAccounts.fields.sortCode')}
            name="sortCode"
            autoComplete="off"
            value={draft.sortCode}
            maxLength={16}
            onChange={(e) => set('sortCode', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label={t('bankAccounts.fields.iban')}
            name="iban"
            autoComplete="off"
            value={draft.iban}
            maxLength={34}
            onChange={(e) => set('iban', e.target.value.toUpperCase())}
          />
          <TextInput
            label={t('bankAccounts.fields.bic')}
            name="bic"
            autoComplete="off"
            value={draft.bic}
            maxLength={11}
            onChange={(e) => set('bic', e.target.value.toUpperCase())}
          />
        </div>
        <SelectInput
          label={t('bankAccounts.fields.currency')}
          name="currencyIsoCode"
          value={draft.currencyIsoCode}
          onChange={(e) => set('currencyIsoCode', e.target.value)}
          options={currencyOptions}
          error={submitted && errs.currency}
          errorMessage={t('bankAccounts.validation.currencyRequired')}
        />
        {/* Only bites when the organisation holds more than one account in the
            same currency — with a single account documents use it regardless. */}
        <div>
          <Switch
            label={t('bankAccounts.fields.defaultForCurrency', {
              code: draft.currencyIsoCode.trim().toUpperCase() || '---',
            })}
            name="isDefaultForCurrency"
            checked={draft.isDefaultForCurrency}
            enabledLabel={t('bankAccounts.fields.defaultForCurrencyEnabled')}
            disabledLabel={t('bankAccounts.fields.defaultForCurrencyDisabled')}
            onChange={(e) => set('isDefaultForCurrency', e.currentTarget.checked)}
          />
          <p className="mt-0.5 text-[11px] text-slate-400">
            {t('bankAccounts.fields.defaultForCurrencyHelp')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('bankAccounts.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('bankAccounts.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('bankAccounts.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact currency-code pill used on the closed-row summary. */
function CurrencyBadge({ code }: { code: string }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-inset ring-purple-200">
      {code}
    </span>
  );
}
